import { useCallback, useEffect, useRef, useState } from 'react';

import { triggerThemeDeploy, getPipelineStatus, PipelineState, PipelineResult } from '@src/services/storefront';
import type { Pipeline, ThemeDeploySelector } from '@src/services/storefront';

const POLL_INTERVAL_MS = 5000;
// Upper bound on polling a single run. A run that never reaches a terminal state (e.g. parked in
// PAUSED) would otherwise poll forever with the buttons disabled; past this we stop and surface a
// timeout so the UI recovers. The backend has its own deploy-claim TTL — this is the FE backstop.
const DEPLOY_DEADLINE_MS = 30 * 60 * 1000;

// Resume support: the active run lives only in component state, so a browser reload loses track of
// it even though the backend deploy is still running (and still holds its claim). We persist the
// triggered run's uuid per partner and, on mount, re-poll it — so an accidental refresh rejoins the
// in-progress deploy (buttons stay disabled) instead of hiding it and re-enabling the deploy button.
// sessionStorage (not local): scoped to the tab/session. Access is wrapped because it can throw
// (privacy mode / quota); resume is best-effort and must never block or break a deploy.
const RESUME_KEY_PREFIX = 'storefront:activeDeploy:';
const resumeKey = (partnerCode: string): string => `${RESUME_KEY_PREFIX}${partnerCode}`;

const readResumeUuid = (partnerCode: string): string | undefined => {
  try {
    const raw = sessionStorage.getItem(resumeKey(partnerCode));
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as { uuid?: unknown };
    return typeof parsed.uuid === 'string' ? parsed.uuid : undefined;
  } catch {
    return undefined;
  }
};

const writeResumeUuid = (partnerCode: string, uuid: string): void => {
  try {
    sessionStorage.setItem(resumeKey(partnerCode), JSON.stringify({ uuid }));
  } catch {
    // best-effort — see note above
  }
};

const clearResumeUuid = (partnerCode: string): void => {
  try {
    sessionStorage.removeItem(resumeKey(partnerCode));
  } catch {
    // best-effort — see note above
  }
};

/**
 * A pipeline is terminal once Bitbucket reports it finished — a COMPLETED/STOPPED state, or any
 * `result` (result is omitted while running, present at terminal, e.g. FAILED on the prod version
 * guard). Polling must run to terminal so a FAILED prod deploy is surfaced, not fire-and-forget.
 * Exported so the run-state chip can distinguish a terminal no-result run from a still-running one.
 */
export const isTerminal = (pipeline: Pipeline): boolean =>
  pipeline.state === PipelineState.Completed ||
  pipeline.state === PipelineState.Stopped ||
  pipeline.result !== undefined;

// A run ended in a versions-changing outcome — succeeded, or completed without an explicit result
// (treated as done). STOPPED / FAILED / ERROR did not publish new versions, so they don't refetch.
const succeeded = (pipeline: Pipeline): boolean =>
  pipeline.result === PipelineResult.Successful ||
  (pipeline.result === undefined && pipeline.state === PipelineState.Completed);

interface TrackedRun {
  // Monotonic per trigger. The poll effect keys on this (NOT the pipeline uuid) so a re-triggered
  // run always restarts polling even when the backend/mock reuses the uuid.
  id: number;
  // The partner the run belongs to, so a partner switch abandons it instead of cross-polling.
  partnerCode: string;
  pipeline: Pipeline;
}

export interface UseThemeDeployResult {
  /** The current run's pipeline for the selected partner, or undefined if none is being tracked. */
  pipeline: Pipeline | undefined;
  /** A trigger is in flight OR a run is still polling — disable the deploy buttons while true. */
  isDeploying: boolean;
  /**
   * An async status-tracking (poll) failure — the run may still be progressing on the backend, but
   * the dashboard lost track of it. Trigger failures are thrown from `deploy` (for the caller to
   * notify), not stored here, so this is exclusively the poll channel.
   */
  error: Error | undefined;
  /** Trigger a deploy for the given selector. Rejects (for the caller to notify, incl. 409). */
  deploy: (selector: ThemeDeploySelector) => Promise<void>;
}

/**
 * Triggers a theme deploy and polls its status to a terminal state, keeping `isDeploying` true for
 * the whole run so the UI can disable the buttons and show live progress. Calls `onSettled` once a
 * run reaches a versions-changing terminal outcome (so the page can re-fetch versions).
 *
 * The run is tracked as an explicit `{ id, partnerCode, pipeline }` record rather than inferring
 * "is a run live?" from the pipeline shape: the poll effect keys on the run id (so a re-trigger
 * always restarts polling), the run is scoped to its partner (so a partner switch never cross-polls
 * a stale uuid), and `error` is cleared only once a NEW run exists — so a failed retry can't leave
 * a non-terminal pipeline with no interval, which previously wedged every button.
 */
export const useThemeDeploy = (
  partnerCode: string | undefined,
  onSettled?: () => void,
): UseThemeDeployResult => {
  const [run, setRun] = useState<TrackedRun | undefined>(undefined);
  const [isTriggering, setIsTriggering] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const nextRunId = useRef<number>(0);

  // Keep the latest callback without making it a poll-effect dependency. Written in an effect (not
  // during render) so an inline callback can't leave a stale ref.
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSettledRef.current = onSettled;
  }, [onSettled]);

  // Only a run that belongs to the selected partner is "active"; a partner switch makes it inert
  // immediately (same render), so nothing cross-polls the previous partner's uuid.
  const activeRun = run && run.partnerCode === partnerCode ? run : undefined;
  const pipeline = activeRun?.pipeline;
  const running = !!pipeline && !isTerminal(pipeline) && !error;
  const isDeploying = isTriggering || running;

  // A partner switch abandons any in-flight error banner (it belonged to the old partner's run).
  useEffect(() => {
    setError(undefined);
  }, [partnerCode]);

  // Resume an in-progress run after a reload: if we are not already tracking a run for this partner
  // but sessionStorage holds one, re-poll its status and adopt it while still non-terminal. A
  // stale/terminal/unknown uuid is dropped so it can't resurrect a finished run. Keyed on
  // partnerCode only (resume once per partner); `run` is read but intentionally not a dependency —
  // a fresh trigger sets `run` without changing partnerCode, so this must not re-run and clobber it.
  useEffect(() => {
    if (!partnerCode || (run && run.partnerCode === partnerCode)) {
      return undefined;
    }
    const uuid = readResumeUuid(partnerCode);
    if (!uuid) {
      return undefined;
    }
    let cancelled = false;
    getPipelineStatus(partnerCode, uuid)
      .then((pipeline) => {
        if (cancelled) {
          return;
        }
        if (isTerminal(pipeline)) {
          clearResumeUuid(partnerCode);
          return;
        }
        nextRunId.current += 1;
        setRun({ id: nextRunId.current, partnerCode, pipeline });
      })
      .catch(() => {
        // Unknown/expired run (e.g. a 404) — drop it so we don't keep trying to resume a dead run.
        if (!cancelled) {
          clearResumeUuid(partnerCode);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [partnerCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const deploy = useCallback(
    async (selector: ThemeDeploySelector) => {
      if (!partnerCode || isDeploying) {
        return;
      }
      setIsTriggering(true);
      try {
        const next = await triggerThemeDeploy(partnerCode, selector);
        nextRunId.current += 1;
        setRun({ id: nextRunId.current, partnerCode, pipeline: next });
        // Persist the run so a browser reload can resume polling it instead of losing the run.
        writeResumeUuid(partnerCode, next.uuid);
        // Clear a prior poll error ONLY now that a new run exists to track. Clearing it before the
        // trigger resolved would re-open the wedge if the trigger then rejected (e.g. a 409 while
        // the previous run still holds the backend deploy claim).
        setError(undefined);
      } finally {
        // A trigger failure propagates to the caller (the page notifies 409 vs generic); it is not
        // stored in `error` (the poll channel) and does not install a run, so nothing wedges.
        setIsTriggering(false);
      }
    },
    [partnerCode, isDeploying],
  );

  // Poll while a non-terminal run exists for this partner. Keyed on the run id so every trigger
  // (re)starts a single interval; it self-clears at terminal, at the deadline, or on unmount /
  // partner or run change.
  useEffect(() => {
    if (!partnerCode || !activeRun || isTerminal(activeRun.pipeline)) {
      return undefined;
    }
    const { uuid } = activeRun.pipeline;
    const startedAt = Date.now();
    let cancelled = false;

    const intervalId = setInterval(async () => {
      if (Date.now() - startedAt > DEPLOY_DEADLINE_MS) {
        clearInterval(intervalId);
        if (!cancelled) {
          setError(new Error('Deploy status timed out — it may still be running on the backend.'));
        }
        return;
      }
      try {
        const next = await getPipelineStatus(partnerCode, uuid);
        if (cancelled) {
          return;
        }
        setRun(prev => (prev && prev.partnerCode === partnerCode ? { ...prev, pipeline: next } : prev));
        if (isTerminal(next)) {
          clearInterval(intervalId);
          // The run is done — drop the resume entry so a later reload can't resurrect a finished run.
          clearResumeUuid(partnerCode);
          if (succeeded(next)) {
            onSettledRef.current?.();
          }
        }
      } catch (caught) {
        if (cancelled) {
          return;
        }
        const err = caught instanceof Error ? caught : new Error(String(caught));
        console.error('useThemeDeploy poll failed:', { partnerCode, uuid, error: err.message });
        setError(err);
        clearInterval(intervalId);
      }
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [partnerCode, activeRun?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { pipeline, isDeploying, error, deploy };
};
